/**
 * Product guides — the in-product education that introduces a new surface.
 *
 * A guide is unresolved, completed, or skipped. Completed and skipped both suppress the guide; they
 * stay distinct because the only interesting question about a tour is where people leave it.
 */

export const GUIDE_STATUSES = {
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  SKIPPED: 'skipped'
} as const;

export type GuideStatus = (typeof GUIDE_STATUSES)[keyof typeof GUIDE_STATUSES];

/** A guide in one of these is done with the current version and no longer offered. */
export const TERMINAL_GUIDE_STATUSES = [
  GUIDE_STATUSES.COMPLETED,
  GUIDE_STATUSES.SKIPPED
] as const satisfies readonly GuideStatus[];

export function isTerminalGuideStatus(status: string): boolean {
  return (TERMINAL_GUIDE_STATUSES as readonly string[]).includes(status);
}

/**
 * How a guide puts itself on screen — the client's concern, since the server cannot know which page
 * someone is on or whether an anchored element is visible. Invoked guides are opened from a help
 * affordance and bypass ordering and suppression, so replaying one on request does not fight the
 * rule that keeps it from showing twice.
 */
export const GUIDE_PRESENTATIONS = {
  AUTOMATIC: 'automatic',
  ANCHORED: 'anchored',
  INVOKED: 'invoked'
} as const;

export type GuidePresentation = (typeof GUIDE_PRESENTATIONS)[keyof typeof GUIDE_PRESENTATIONS];

/**
 * How long an interrupting guide keeps the next one off the screen, measured from the last time a
 * guide actually reached it. One number for everyone rather than a field on a guide — the point is
 * to protect the person from the total volume, and two guides could otherwise disagree about how
 * often the same person may be interrupted.
 */
export const GUIDE_THROTTLE_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Who qualifies — the server's concern. A predicate is a tagged object rather than a boolean or a
 * function so a second type is one branch in the evaluator, not an edit to every guide. A predicate
 * the API cannot answer inline belongs to a scheduled job instead, which is the only difference
 * between the inline and precomputed tiers at the point of authoring.
 */
export const GUIDE_PREDICATES = {
  ALWAYS: 'always'
} as const;

export type GuidePredicate = { type: typeof GUIDE_PREDICATES.ALWAYS };

export interface GuideDefinition {
  name: string;
  presentation: GuidePresentation;
  predicate: GuidePredicate;
  /** Ordered and linear. Stable keys, not positions — progress stores the key of the last finished step. */
  steps: readonly string[];
  /** Lower sorts first among guides eligible at the same moment. */
  priority: number;
  /**
   * The version lever. A guide counts as resolved only when it was resolved later than this date,
   * so reworking a guide means bumping it — no data migration and no second row.
   */
  publishedAt: string;
}

export const GUIDES = {
  PATHWAY_OVERVIEW: {
    name: 'pathway-overview',
    presentation: GUIDE_PRESENTATIONS.ANCHORED,
    predicate: { type: GUIDE_PREDICATES.ALWAYS },
    steps: ['intro'],
    priority: 100,
    publishedAt: '2026-08-16'
  },
  ACTION_PANEL_MOVABLE: {
    name: 'movable-action-panel',
    presentation: GUIDE_PRESENTATIONS.ANCHORED,
    predicate: { type: GUIDE_PREDICATES.ALWAYS },
    steps: ['intro'],
    priority: 200,
    publishedAt: '2026-08-20'
  }
} as const satisfies Record<string, GuideDefinition>;

export const GUIDE_DEFINITIONS: readonly GuideDefinition[] = Object.values(GUIDES);

export function findGuide(name: string): GuideDefinition | undefined {
  return GUIDE_DEFINITIONS.find(guide => guide.name === name);
}
